"use client";

import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { ReactNode } from "react";
import { Button } from "../../button";
import { IntentProgressCircle } from "../../intent-ui/progress-circle";
import { cn } from "../../../lib/cn";
import { TIMED_UNDO_DEFAULT_DURATION_MS } from "./use-timed-undo";

export interface TimedUndoBarProps {
	/** When false, renders nothing (exit animation still plays via presence). */
	open: boolean;
	/** Short status copy (e.g. “Notification deleted”). */
	label: ReactNode;
	undoLabel?: ReactNode;
	remainingMs: number;
	durationMs?: number;
	/** 0–100 countdown (100 → 0). Computed from remaining if omitted. */
	remainingPercent?: number;
	onUndo: () => void;
	className?: string;
}

/**
 * Auto-width frosted chip for timed undo — position with `absolute top-* right-*`
 * over the host panel content (`relative` ancestor required).
 * Scale/fades in when the window starts and out when it ends / is undone.
 */
export function TimedUndoBar({
	open,
	label,
	undoLabel = "Undo",
	remainingMs,
	durationMs = TIMED_UNDO_DEFAULT_DURATION_MS,
	remainingPercent: remainingPercentProp,
	onUndo,
	className,
}: TimedUndoBarProps) {
	const reducedMotion = useReducedMotion();
	const remainingPercent =
		remainingPercentProp ??
		Math.max(0, Math.min(100, (remainingMs / durationMs) * 100));
	const timerSeconds = remainingMs / 1000;

	return (
		<AnimatePresence>
			{open ? (
				<motion.div
					key="timed-undo-bar"
					role="status"
					aria-live="polite"
					data-timed-undo-bar=""
					initial={
						reducedMotion
							? false
							: { opacity: 0, scale: 0.92 }
					}
					animate={{ opacity: 1, scale: 1 }}
					exit={
						reducedMotion
							? undefined
							: { opacity: 0, scale: 0.92 }
					}
					transition={{
						duration: reducedMotion ? 0 : 0.18,
						ease: "easeOut",
					}}
					className={cn(
						"pointer-events-auto absolute top-2 right-2 z-40 origin-top-right",
						"flex w-auto max-w-[min(calc(100%-1rem),18rem)] items-center gap-2",
						"rounded-xl border border-border/50 px-2.5 py-1.5 shadow-lg",
						"bg-background/70 text-foreground backdrop-blur-md",
						className,
					)}
				>
					<IntentProgressCircle
						value={remainingPercent}
						size={28}
						strokeWidth={3}
						progressStrokeWidth={3}
						replaceWithCheckWhenComplete={false}
						centerMode="timer"
						timerSeconds={timerSeconds}
						centerLabelClassName="text-foreground text-shadow-none"
						showTooltip={false}
						className="shrink-0"
					/>
					<span className="min-w-0 flex-1 truncate text-xs font-medium leading-snug">
						{label}
					</span>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="h-7 shrink-0 px-2 text-xs font-black uppercase tracking-wide"
						onClick={onUndo}
					>
						{undoLabel}
					</Button>
				</motion.div>
			) : null}
		</AnimatePresence>
	);
}
